import { useState } from 'react';

import { validator, isValid, errors } from 'react-validator';
import { useHistory } from 'react-router';

import {
  useChangePasswordMutation,
  ChangePasswordMutationVariables,
} from '@school_ify/schoolify-repository/src/shared/types';

import Button from 'components/Button/Button';
import Errors from 'components/Errors/Errors';
import Input from 'components/Input/Text/Input';

import './Password.scoped.scss';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ShowPassword } from 'assets/icons/show-password.svg';
import { ReactComponent as HidePassword } from 'assets/icons/hide-password.svg';

const Password = () => {
  const { t } = useTranslation('common');
  const history = useHistory();

  const [model, setModel] = useState<ChangePasswordMutationVariables>({
    new_password: '',
    old_password: '',
  });
  const [validation, setValidation] = useState({});

  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [changePassword] = useChangePasswordMutation();

  const inputStyle = {
    backgroundColor: '#fff',
    border: '1px solid #213960',
  };

  const handleSave = async () => {
    try {
      validator(
        { model },
        {
          'model.old_password': ['required', 'min:8'],
          'model.new_password': ['required', 'min:8'],
        },
        {
          'model.old_password.required': t('common:Password_Reset.old_password_required'),
          'model.new_password.required': t('common:Password_Reset.new_password_required'),
        }
      );

      setValidation(errors());
      if (!isValid()) {
        return;
      }
      if (model.new_password !== confirmPassword) {
        setConfirmPasswordError(t('common:Password_Reset.no_match'));
        return;
      }
      if (model.old_password === model.new_password) {
        setNewPasswordError(t('common:Password_Reset.same_password'));
        return;
      }
      await changePassword({ variables: model });
      setConfirmPasswordError('');
      setNewPasswordError('');
      history.push('/');
    } catch (error) {
      setConfirmPasswordError(t(error.message));
      setNewPasswordError(t(error.message));
    }
  };

  return (
    <>
      {/* This is a workaround - since browsers autosuggest filling in passwords, this prevents user email being filled into search input */}
      <input className="input-hidden" />

      <div className="input-container">
        <Input
          placeholder={t('Password')}
          label={t('common:Password_Reset.Enter_current_password')}
          name="old_password"
          grey
          value={model.old_password}
          onChange={value => setModel({ ...model, old_password: value })}
          type={!showCurrentPassword ? 'password' : 'text'}
          style={inputStyle}
          onIconClicked={() => setShowCurrentPassword(!showCurrentPassword)}
          appendIcon={!showCurrentPassword ? <HidePassword /> : <ShowPassword />}
        />
      </div>
      <Errors errors={validation} prop={'model.old_password'} />

      <div className="input-container">
        <Input
          label={t('common:Password_Reset.Enter_new_password')}
          name="new_password"
          grey
          value={model.new_password}
          onChange={value => setModel({ ...model, new_password: value })}
          type={!showNewPassword ? 'password' : 'text'}
          style={inputStyle}
          onIconClicked={() => setShowNewPassword(!showNewPassword)}
          appendIcon={!showNewPassword ? <HidePassword /> : <ShowPassword />}
        />
      </div>
      <Errors errors={validation} prop={'model.new_password'} />
      {newPasswordError && (
        <label htmlFor={'confirm_password'} className={'text-small mt-1'} style={{ color: 'red' }}>
          {newPasswordError}
        </label>
      )}

      <div className="input-container">
        <Input
          label={t('common:Password_Reset.Confirm_new_password')}
          grey
          name="confirm_password"
          value={confirmPassword}
          onChange={value => setConfirmPassword(value)}
          type={!showConfirmPassword ? 'password' : 'text'}
          style={inputStyle}
          onIconClicked={() => setShowConfirmPassword(!showConfirmPassword)}
          appendIcon={!showConfirmPassword ? <HidePassword /> : <ShowPassword />}
        />
      </div>
      {confirmPasswordError && (
        <label htmlFor={'confirm_password'} className={'text-small mt-1'} style={{ color: 'red' }}>
          {confirmPasswordError}
        </label>
      )}
      <div className="input-container" />
      <Button
        onClick={handleSave}
        round
        outlined
        style={{
          backgroundColor: '#5CD79E',
          width: '150px',
          color: '#fff',
          opacity: 0.7,
          fontSize: 15,
          marginTop: 30,
          marginBottom: 30,
        }}
      >
        <p>{t('save')}</p>
      </Button>
    </>
  );
};

export default Password;
